import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal } from "../components/Reveal";
import CTABlock from "../components/CTABlock";
import QuoteMiniForm from "../components/QuoteMiniForm";
import { SEO_META, FABRICS } from "../config/site";

export default function Fabrics() {
  const meta = SEO_META.fabrics;
  return (
    <>
      <SEO title={meta.title} description={meta.description} path={meta.path} />
      <section className="border-b border-obsidian/10 bg-bone" data-testid="fabrics-hero">
        <div className="container-site py-16 lg:py-24">
          <Reveal>
            <p className="eyebrow">Fabric Library</p>
            <h1 className="mt-4 max-w-3xl font-display text-4xl font-black leading-[1.06] tracking-tight sm:text-5xl">
              Choose the cloth before you choose the quote.
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-obsidian/60">
              A working guide to the fabrics we manufacture uniforms in most often — with typical GSM ranges so
              you can shortlist materials before requesting a quotation. Final fabric and GSM are confirmed per
              product specification.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="container-site py-16 lg:py-24" data-testid="fabrics-grid">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {FABRICS.map((f, i) => (
            <Reveal key={f.slug} delay={(i % 4) * 0.07}>
              <article id={f.slug} className="group scroll-mt-28 border border-obsidian/10 bg-white" data-testid={`fabric-card-${f.slug}`}>
                <div className="overflow-hidden">
                  <img src={f.img} alt={f.alt} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]" />
                </div>
                <div className="p-6">
                  <span className="inline-block border border-cleef/30 bg-cleef-light px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-cleef">
                    {f.gsm}
                  </span>
                  <h2 className="mt-3 font-display text-lg font-bold tracking-tight">{f.name}</h2>
                  <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-obsidian/45">Best for: {f.bestFor}</p>
                  <p className="mt-3 text-sm leading-relaxed text-obsidian/60">{f.desc}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-16 grid gap-10 border border-obsidian/10 bg-white p-8 sm:p-12 lg:grid-cols-2" data-testid="fabrics-guide">
            <div>
              <p className="eyebrow">How to read GSM</p>
              <h2 className="mt-3 font-display text-2xl font-extrabold leading-[1.1] tracking-tight sm:text-3xl">
                Fabric weight, in plain terms
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-obsidian/60">
                GSM (grams per square metre) describes how heavy a fabric is. Lower GSM feels lighter and cooler;
                higher GSM feels more structured and durable. The right weight depends on the garment, the
                climate your team works in and how often the uniform is washed.
              </p>
            </div>
            <ul className="grid content-center gap-px border border-obsidian/10 bg-obsidian/10">
              {[
                ["110–160", "Lightweight — shirts, blouses, warm climates"],
                ["160–220", "Mid-weight — polos, tees, everyday uniforms"],
                ["220–300", "Heavy — trousers, aprons, structured workwear"],
                ["280+", "Winter weight — fleece, hoodies, outerwear"],
              ].map(([range, use]) => (
                <li key={range} className="flex items-center gap-4 bg-white px-6 py-4">
                  <span className="w-20 shrink-0 font-mono text-sm font-semibold text-cleef">{range}</span>
                  <span className="text-sm text-obsidian/70">{use}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-16 grid gap-10 lg:grid-cols-2" data-testid="fabrics-quote">
            <div className="flex flex-col justify-center">
              <p className="eyebrow">Shortlisted a fabric?</p>
              <h2 className="mt-3 font-display text-2xl font-extrabold leading-[1.1] tracking-tight sm:text-3xl">
                Send your requirement with the fabric name
              </h2>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-obsidian/60">
                Mention the fabric and GSM you have in mind — or simply describe the garment and working
                conditions, and we will recommend the right material in your quotation.
              </p>
              <Link to="/custom-manufacturing" className="mt-6 inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.16em] text-obsidian transition-colors hover:text-cleef" data-testid="fabrics-custom-link">
                See customization options <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <QuoteMiniForm productType="Fabric-guided uniform enquiry" heading="Request a fabric-based quotation" testId="fabrics-quote-form" />
          </div>
        </Reveal>
      </section>
      <CTABlock />
    </>
  );
}
